import { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { Search, MapPin, Calendar, DollarSign } from 'lucide-react';
import { format } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function History() {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    fetchJobs();
  }, []);

  const fetchJobs = async () => {
    try {
      const response = await axios.get(`${API}/jobs`);
      // Sort by created_at descending
      const sorted = response.data.sort((a, b) => 
        new Date(b.created_at) - new Date(a.created_at)
      );
      setJobs(sorted);
    } catch (error) {
      console.error('Failed to fetch jobs:', error);
      toast.error('Failed to load job history');
    } finally {
      setLoading(false);
    }
  };

  const filteredJobs = jobs.filter(job => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return (
      job.client_name.toLowerCase().includes(searchLower) ||
      job.address?.toLowerCase().includes(searchLower) ||
      job.description.toLowerCase().includes(searchLower)
    );
  });

  const formatDate = (dateStr) => {
    try {
      return format(new Date(dateStr), 'MMM d, yyyy');
    } catch {
      return dateStr;
    }
  };

  const statusColors = {
    quote: 'bg-blue-100 text-blue-700',
    scheduled: 'bg-amber-100 text-amber-700',
    paid: 'bg-emerald-100 text-emerald-700'
  };

  const statusLabels = {
    quote: 'Quote',
    scheduled: 'Scheduled',
    paid: 'Paid'
  };

  return (
    <div className="p-4 animate-fade-in" data-testid="history-page">
      {/* Header */}
      <div className="mb-6">
        <h1 className="font-heading text-3xl font-bold text-slate-dark">
          Job History
        </h1>
        <p className="text-slate-500">Search past jobs and customers</p>
      </div>
      
      {/* Search */}
      <div className="relative mb-6">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
        <Input
          data-testid="history-search"
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name, address, or description..."
          className="h-14 pl-12 bg-white border-2 border-slate-300 text-slate-900 placeholder:text-slate-400 text-lg rounded-lg"
        />
      </div>
      
      {/* Results count */}
      <p className="text-slate-500 text-sm mb-4">
        {filteredJobs.length} {filteredJobs.length === 1 ? 'job' : 'jobs'} found
      </p>
      
      {/* Jobs List */}
      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3, 4, 5].map(i => (
            <div key={i} className="h-24 bg-slate-200 rounded-xl animate-pulse" />
          ))}
        </div>
      ) : filteredJobs.length === 0 ? (
        <div className="text-center py-12">
          <Search className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <h3 className="font-heading text-xl font-bold text-slate-dark mb-2">
            {search ? 'No matches found' : 'No job history'}
          </h3>
          <p className="text-slate-500">
            {search ? 'Try a different search term' : 'Completed jobs will appear here'}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredJobs.map((job, index) => (
            <div
              key={job.id}
              data-testid={`history-job-${job.id}`}
              onClick={() => navigate(`/job/${job.id}`)}
              className="bg-white rounded-xl border-2 border-slate-200 p-4 cursor-pointer hover:border-[#FF5F00] transition-colors animate-slide-up"
              style={{ animationDelay: `${index * 30}ms` }}
            >
              <div className="flex items-start justify-between mb-2">
                <div>
                  <h3 className="font-heading text-lg font-bold text-slate-dark">
                    {job.client_name}
                  </h3>
                  <p className="text-slate-500 text-sm line-clamp-1">
                    {job.description}
                  </p>
                </div>
                <span className={`px-2 py-1 rounded-full text-xs font-bold ${statusColors[job.status]}`}>
                  {statusLabels[job.status]}
                </span>
              </div>
              
              <div className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-4 text-slate-500">
                  {job.address && (
                    <span className="flex items-center gap-1">
                      <MapPin className="w-3 h-3" />
                      <span className="truncate max-w-[120px]">{job.address}</span>
                    </span>
                  )}
                  <span className="flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    {formatDate(job.created_at)}
                  </span>
                </div>
                <span className="font-heading font-bold text-slate-dark flex items-center">
                  <DollarSign className="w-4 h-4" />
                  {job.quote_amount.toLocaleString()}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
